import React, { useEffect, useMemo, useState } from 'react';

import { DndContext } from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { useSnackbar } from 'notistack';
import { Control, Controller, useController, useFieldArray } from 'react-hook-form';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { ArrayFieldItem, Checkbox, Input } from '~/components/Common/Forms/fields';

import ReservationService from '~/services/ReservationService';

import SortableInclusionRow from '../SortableInclusionRow';

interface Props {
  actions?: React.ReactNode;
  control: Control<any>;
  label: string;
  name: string;
  setValue: (name: string, value: unknown, config?: object) => void;
  importOfferInclusions?: () => void | Promise<void>;
  variant?: 'default' | 'offer-builder';
}

function OfferInclusionsSelect({
  actions,
  control,
  label,
  name,
  setValue,
  importOfferInclusions,
  variant = 'default',
}: Props) {
  const [inclusionsCategories, setInclusionsCategories] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const isOfferBuilderVariant = variant === 'offer-builder';

  useEffect(() => {
    setLoading(true);
    ReservationService.getInclusionsCategories()
      .then(({ result }) => setInclusionsCategories(result))
      .catch((e) =>
        enqueueSnackbar(`Failed to fetch offer inclusion categories: ${e?.message}`, { variant: 'warning' }),
      )
      .finally(() => setLoading(false));
  }, []);

  const dataArray = useFieldArray({
    control,
    name,
  });
  const {
    field: { value: dataValue, onChange: onChangeArray },
    fieldState: { error },
  } = useController({
    control,
    name,
  });

  const sortableData = useMemo<Array<string>>(
    () => dataArray.fields?.map((item, index) => `${item.id}-${index}`) ?? [],
    [dataArray.fields],
  );
  function handleDragEnd(event) {
    const { active, over } = event;
    if (active?.id !== over?.id) {
      const oldIndex = sortableData.indexOf(active?.id);
      const newIndex = sortableData.indexOf(over?.id);
      const newArray = arrayMove(dataValue, oldIndex, newIndex);

      setValue(name, newArray);
    }
  }

  const onInputChange = (event, name, index) => {
    setValue(event.target.name, event.target.value);
    updateValue(name, index, event.target.value);
  };

  const updateValue = (name, index, value, rest = {}) => {
    const newArray = [...dataValue];
    const updatedValue = { ...newArray[index], [name]: value, ...rest };

    newArray.splice(index, 1, updatedValue);

    onChangeArray(newArray);
  };

  const onAutocompleteChange = (value, key, index) => {
    const id = `${name}.${index}.${key}`;
    const iconId = `${name}.${index}.icon`;
    const newValue = value?.name ?? '';
    const newIcon = value?.icon ?? '';

    setValue(id, newValue);
    setValue(iconId, newIcon);
    updateValue(key, index, newValue, { icon: newIcon });
  };

  const onCheckboxChange = (event, name, index) => {
    const newArray = [...dataValue];
    const updatedValue = { ...newArray[index], [name]: event.target.checked };

    newArray.splice(index, 1, updatedValue);
    setValue(event.target.name, event.target.checked);

    onChangeArray(newArray);
  };

  const onLuxPlusCheckboxChange = (event, name, index) => {
    const newArray = [...dataValue];
    const mappedValue = event.target.checked ? 'base' : null;
    const updatedValue = { ...newArray[index], [name]: mappedValue };

    newArray.splice(index, 1, updatedValue);
    setValue(event.target.name, event.target.checked);

    onChangeArray(newArray);
  };

  const onRemove = (index: number) => {
    dataArray.remove(index);
    const newArray = [...dataValue];
    newArray.splice(index, 1);
    setValue(name, newArray);
  };

  const onImportInclusionsClick = async () => {
    setLoading(true);
    try {
      await importOfferInclusions();
    } finally {
      setLoading(false);
    }
  };

  const onAddInclusion = () => {
    dataArray.append({
      text: '',
      is_highlighted: false,
    });
  };

  return (
    <Stack>
      <Grid container>
        <Grid item xs>
          <Typography variant="h6" mb={1}>
            {label}
          </Typography>
        </Grid>
        {(importOfferInclusions || actions) && (
          <Grid item>
            <Stack direction="row" spacing={2}>
              {importOfferInclusions && (
                <Button onClick={onImportInclusionsClick} disabled={loading}>
                  Import from base package
                </Button>
              )}
              {actions}
            </Stack>
          </Grid>
        )}
      </Grid>

      <Box
        sx={{
          ...(isOfferBuilderVariant && {
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }),
        }}
      >
        {isOfferBuilderVariant && !!dataArray.fields.length && (
          <Box
            sx={{
              bgcolor: 'grey.50',
              display: { xs: 'none', lg: 'grid' },
              gap: 2,
              gridTemplateColumns: '40px minmax(260px, 1fr) minmax(150px, 220px) 130px 150px 40px',
              px: 2,
              py: 1.5,
            }}
          >
            <Box />
            <Typography variant="overline" sx={{ fontWeight: 700 }}>
              Inclusion
            </Typography>
            <Typography variant="overline" sx={{ fontWeight: 700 }}>
              Category
            </Typography>
            <Typography variant="overline" align="center" sx={{ fontWeight: 700 }}>
              Highlight
            </Typography>
            <Typography variant="overline" align="center" sx={{ fontWeight: 700 }}>
              LuxPlus only
            </Typography>
            <Box />
          </Box>
        )}

        <DndContext onDragEnd={handleDragEnd}>
          <SortableContext items={sortableData}>
            {dataArray.fields.map((item, index) => {
              const inclusionInput = (
                <Controller
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      error={!!fieldState.error}
                      fullWidth
                      label="Inclusion"
                      onChange={(event) => onInputChange(event, 'text', index)}
                      variant="outlined"
                    />
                  )}
                  name={`${name}.${index}.text`}
                  control={control}
                />
              );
              const categoryInput = (
                <Box flexGrow={1}>
                  <Controller
                    render={({ field }) => (
                      <Autocomplete
                        {...field}
                        onChange={(e: any, newValue: string | null) => {
                          onAutocompleteChange(newValue, 'category', index);
                        }}
                        isOptionEqualToValue={(option, value) => option.name === value}
                        value={field.value}
                        options={inclusionsCategories?.sort((a, b) => -b.category.localeCompare(a.category))}
                        groupBy={(option) => option.category}
                        getOptionLabel={(option) => option.name || option}
                        renderInput={(params) => <TextField {...params} label="Category (Icon)" />}
                      />
                    )}
                    name={`${name}.${index}.category`}
                    control={control}
                  />
                  <Input control={control} hidden name={`${name}.${index}.icon`} />
                </Box>
              );

              return (
                <FormControl
                  error={error?.[index]}
                  key={item.id}
                  sx={{
                    display: 'flex',
                    mb: isOfferBuilderVariant ? 0 : 2,
                    ...(isOfferBuilderVariant && {
                      borderTop: 1,
                      borderColor: 'divider',
                      px: 2,
                      py: 2,
                    }),
                  }}
                >
                  {isOfferBuilderVariant ? (
                    <SortableInclusionRow id={`${item.id}-${index}`}>
                      <Box
                        sx={{
                          alignItems: 'center',
                          display: 'grid',
                          flexGrow: 1,
                          gap: 2,
                          gridTemplateColumns: {
                            xs: '1fr',
                            lg: 'minmax(260px, 1fr) minmax(150px, 220px) 130px 150px 40px',
                          },
                        }}
                      >
                        {inclusionInput}
                        {categoryInput}
                        <Stack direction="row" justifyContent={{ xs: 'flex-start', lg: 'center' }}>
                          <FormControlLabel
                            label="Highlight"
                            sx={{
                              m: 0,
                              '& .MuiFormControlLabel-label': { display: { xs: 'block', lg: 'none' } },
                            }}
                            control={
                              <Switch
                                name={`${name}.${index}.is_highlighted`}
                                checked={!!dataValue?.[index]?.is_highlighted}
                                inputProps={{ 'aria-label': 'Highlight inclusion' }}
                                onChange={(event) => onCheckboxChange(event, 'is_highlighted', index)}
                              />
                            }
                          />
                        </Stack>
                        <Stack direction="row" justifyContent={{ xs: 'flex-start', lg: 'center' }}>
                          <FormControlLabel
                            label="LuxPlus only"
                            sx={{
                              m: 0,
                              '& .MuiFormControlLabel-label': { display: { xs: 'block', lg: 'none' } },
                            }}
                            control={
                              <Switch
                                name={`${name}.${index}.lux_plus_tier`}
                                checked={!!dataValue?.[index]?.lux_plus_tier}
                                inputProps={{ 'aria-label': 'LuxPlus only inclusion' }}
                                onChange={(event) => onLuxPlusCheckboxChange(event, 'lux_plus_tier', index)}
                              />
                            }
                          />
                        </Stack>
                        <IconButton onClick={() => onRemove(index)} color="error" sx={{ justifySelf: 'center' }}>
                          <DeleteIcon fontSize="medium" />
                        </IconButton>
                      </Box>
                    </SortableInclusionRow>
                  ) : (
                    <ArrayFieldItem itemKey={item.id} index={index}>
                      <Stack flexGrow={1} direction="row" spacing={2} alignItems="center">
                        <Box flexGrow={2}>{inclusionInput}</Box>
                        {categoryInput}
                        <Box>
                          <Checkbox
                            control={control}
                            label="Highlight"
                            name={`${name}.${index}.is_highlighted`}
                            onChange={(event) => onCheckboxChange(event, 'is_highlighted', index)}
                          />
                          <Checkbox
                            control={control}
                            label="LuxPlus Exclusive"
                            name={`${name}.${index}.lux_plus_tier`}
                            onChange={(event) => onLuxPlusCheckboxChange(event, 'lux_plus_tier', index)}
                          />
                        </Box>
                        <Box>
                          <IconButton onClick={() => onRemove(index)} color="error">
                            <DeleteIcon fontSize="medium" />
                          </IconButton>
                        </Box>
                      </Stack>
                    </ArrayFieldItem>
                  )}
                </FormControl>
              );
            })}
            {!dataArray.fields.length && (
              <Box
                sx={{
                  p: isOfferBuilderVariant ? 3 : 2,
                  width: 1,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {!loading && <Typography variant="body2">Empty</Typography>}
                {loading && <CircularProgress />}
              </Box>
            )}
            <Box
              sx={{
                width: 1,
                display: 'flex',
                justifyContent: 'flex-end',
                ...(isOfferBuilderVariant && {
                  borderTop: 1,
                  borderColor: 'divider',
                  p: 2,
                }),
              }}
            >
              {isOfferBuilderVariant ? (
                <Button onClick={onAddInclusion} startIcon={<AddIcon />}>
                  Add inclusion
                </Button>
              ) : (
                <IconButton onClick={onAddInclusion} color="primary">
                  <AddIcon />
                </IconButton>
              )}
            </Box>
          </SortableContext>
        </DndContext>
      </Box>
    </Stack>
  );
}

export default OfferInclusionsSelect;
